import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowRight,
  FiUserCheck,
  FiGrid,
  FiUsers,
  FiBook,
  FiClipboard,
  FiCalendar,
  FiCheck
} from 'react-icons/fi';
import './LandingPage.css';

const STEP_ICONS = [FiUserCheck, FiGrid, FiUsers, FiBook, FiClipboard, FiCalendar];

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const stepsRef = useRef(null);
  const [stepsVisible, setStepsVisible] = useState(false);

  useEffect(() => {
    const el = stepsRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStepsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const steps = [1, 2, 3, 4, 5, 6].map((n) => ({
    n,
    Icon: STEP_ICONS[n - 1],
    title: t(`landing.step${n}Title`),
    desc: t(`landing.step${n}Desc`)
  }));

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language?.startsWith('vi') ? 'en' : 'vi');
  };

  return (
    <div className="landing">
      <header className="lp-topbar">
        <span className="lp-topbar-brand">{t('nav.appName')}</span>
        <div className="lp-topbar-actions">
          <button type="button" className="lp-lang" onClick={toggleLang}>
            {i18n.language?.startsWith('vi') ? 'EN' : 'VI'}
          </button>
          <Link to="/login" className="lp-link-quiet">{t('landing.login')}</Link>
          <Link to="/register" className="lp-btn lp-btn-sm">{t('landing.register')}</Link>
        </div>
      </header>

      {/* —— Hero: one composition —— */}
      <section className="lp-hero" aria-label={t('nav.appName')}>
        <div className="lp-hero-plane" aria-hidden="true">
          <div className="lp-timetable">
            <div className="lp-tt-head">
              <span /><span /><span /><span /><span /><span />
            </div>
            {Array.from({ length: 6 }).map((_, row) => (
              <div className="lp-tt-row" key={row}>
                <span className="lp-tt-period">{row + 1}</span>
                {Array.from({ length: 5 }).map((__, col) => (
                  <span
                    key={col}
                    className={`lp-tt-cell c${(row + col) % 5}`}
                    style={{ animationDelay: `${0.35 + (row * 5 + col) * 0.04}s` }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="lp-hero-copy">
          <p className="lp-brand">{t('nav.appName')}</p>
          <h1 className="lp-headline">{t('landing.headline')}</h1>
          <p className="lp-lede">{t('landing.lede')}</p>
          <div className="lp-cta">
            <Link to="/register" className="lp-btn lp-btn-primary">
              {t('landing.ctaStart')}
              <FiArrowRight />
            </Link>
            <Link to="/login" className="lp-btn lp-btn-ghost">
              {t('landing.ctaLogin')}
            </Link>
          </div>
        </div>
      </section>

      {/* —— Steps —— */}
      <section className="lp-steps" id="quy-trinh" ref={stepsRef}>
        <div className="lp-section-inner">
          <h2 className="lp-section-title">{t('landing.stepsTitle')}</h2>
          <p className="lp-section-lede">{t('landing.stepsLede')}</p>

          <ol className={`lp-step-list ${stepsVisible ? 'is-visible' : ''}`}>
            {steps.map(({ n, Icon, title, desc }) => (
              <li
                key={n}
                className="lp-step"
                style={{ '--i': n }}
              >
                <div className="lp-step-index">
                  <span className="lp-step-num">{String(n).padStart(2, '0')}</span>
                  <Icon className="lp-step-icon" aria-hidden />
                </div>
                <div className="lp-step-text">
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* —— Principles —— */}
      <section className="lp-principles">
        <div className="lp-section-inner">
          <h2 className="lp-section-title">{t('landing.principlesTitle')}</h2>
          <p className="lp-section-lede">{t('landing.principlesLede')}</p>
          <ul className="lp-principle-list">
            {[1, 2, 3].map((n) => (
              <li key={n}>
                <FiCheck aria-hidden />
                <span>{t(`landing.principle${n}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* —— Closing CTA —— */}
      <section className="lp-close">
        <div className="lp-section-inner lp-close-inner">
          <h2>{t('landing.closeTitle')}</h2>
          <p>{t('landing.closeLede')}</p>
          <div className="lp-cta">
            <Link to="/register" className="lp-btn lp-btn-primary">
              {t('landing.ctaStart')}
              <FiArrowRight />
            </Link>
            <Link to="/login" className="lp-btn lp-btn-ghost-light">
              {t('landing.ctaLogin')}
            </Link>
          </div>
        </div>
      </section>

      <footer className="lp-footer">
        <span>{t('nav.appName')}</span>
        <span className="lp-footer-dot" aria-hidden />
        <span>{t('landing.footerTag')}</span>
      </footer>
    </div>
  );
};

export default LandingPage;
